import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Volume2, RotateCcw, ChevronLeft, ChevronRight, CheckCircle2, Lightbulb, ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSounds } from '@/hooks/useSounds';
import { speakText } from '@/utils/speechUtils';

export default function FlashCard({ 
  cards, // [{front: 'Hello', back: 'Merhaba', example: '...', audio: '...'}, ...]
  title,
  onAnswer,
  onComplete,
  nextLessonInfo,
  onNextLesson,
  onBackToUnit,
  disabled = false 
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [knownCards, setKnownCards] = useState([]);
  const [reviewCards, setReviewCards] = useState([]);
  const [isCompleted, setIsCompleted] = useState(false);
  const { playCardFlip, playCorrect, playLessonComplete, playClick } = useSounds();

  const currentCard = cards[currentIndex];
  const progress = ((knownCards.length + reviewCards.length) / cards.length) * 100;

  const handleFlip = () => {
    if (disabled) return;
    playCardFlip();
    setIsFlipped(!isFlipped);
  };

  const handleKnown = () => {
    if (!isFlipped || disabled) return;
    playCorrect();
    const newKnownCards = [...knownCards, currentIndex];
    setKnownCards(newKnownCards);
    goToNext(newKnownCards, reviewCards);
  };

  const handleReview = () => {
    if (!isFlipped || disabled) return;
    playClick();
    const newReviewCards = [...reviewCards, currentIndex];
    setReviewCards(newReviewCards);
    goToNext(knownCards, newReviewCards);
  };

  const goToNext = (currentKnownCards, currentReviewCards) => {
    setIsFlipped(false);
    
    // Sonraki gösterilmemiş kartı bul - güncellenmiş state'leri kullan
    // currentIndex zaten currentKnownCards veya currentReviewCards içinde olacak
    const shownCards = [...currentKnownCards, ...currentReviewCards];
    const nextUnshown = cards.findIndex((_, idx) => !shownCards.includes(idx));
    
    if (nextUnshown !== -1) {
      setTimeout(() => setCurrentIndex(nextUnshown), 300);
    } else {
      // Tüm kartlar gösterildi
      setIsCompleted(true);
      playLessonComplete();
      const score = currentKnownCards.length / cards.length;
      setTimeout(() => {
        onAnswer?.(score >= 0.7, score);
        // Ders tamamlandı, API'ye kaydet
        if (onComplete) {
          onComplete();
        }
      }, 500);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0 && !disabled) {
      setIsFlipped(false);
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleReset = () => {
    setCurrentIndex(0);
    setIsFlipped(false);
    setKnownCards([]);
    setReviewCards([]);
    setIsCompleted(false);
  };

  // speakText artık utils'ten import ediliyor

  if (isCompleted) {
    const accuracy = Math.round((knownCards.length / cards.length) * 100);
    return (
      <div className="w-full max-w-lg mx-auto text-center">
        <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100">
          <div className={cn(
            'w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4',
            accuracy >= 70 ? 'bg-green-100' : 'bg-amber-100'
          )}>
            <CheckCircle2 className={cn(
              'w-10 h-10',
              accuracy >= 70 ? 'text-green-500' : 'text-amber-500'
            )} />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Ders Tamamlandı! 🎉</h2>
          <p className="text-gray-500 mb-4">{cards.length} kartın {knownCards.length} tanesini biliyorsun</p>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-green-50 rounded-xl p-4">
              <p className="text-3xl font-bold text-green-600">{knownCards.length}</p>
              <p className="text-sm text-green-600">Biliyorum</p>
            </div>
            <div className="bg-amber-50 rounded-xl p-4">
              <p className="text-3xl font-bold text-amber-600">{reviewCards.length}</p>
              <p className="text-sm text-amber-600">Tekrar Et</p>
            </div>
          </div>

          {/* Sonraki Ders Bilgisi */}
          {nextLessonInfo && !nextLessonInfo.isLevelComplete && (
            <div className="bg-blue-50 rounded-xl p-4 mb-4">
              <p className="text-xs text-blue-600 font-medium mb-1">
                {nextLessonInfo.isNewUnit ? `📚 Yeni Ünite: ${nextLessonInfo.unitTitle}` : 'Sonraki Ders'}
              </p>
              <p className="text-sm font-bold text-blue-800">{nextLessonInfo.lesson?.title}</p>
            </div>
          )}

          <div className="space-y-3">
            {/* Sonraki Derse Geç - sadece tek başına FlashCard dersleri için */}
            {onNextLesson && (
              <Button 
                onClick={onNextLesson} 
                className="w-full py-4 bg-emerald-500 hover:bg-emerald-600"
              >
                {nextLessonInfo?.isLevelComplete 
                  ? 'Seviyeyi Tamamla 🏆' 
                  : nextLessonInfo?.isUnitComplete 
                    ? 'Sonraki Üniteye Geç' 
                    : 'Sonraki Derse Geç'
                }
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            )}
            
            {/* Mixed lesson içindeyse - Devam Et butonu */}
            {!onNextLesson && !onBackToUnit && onComplete && (
              <Button 
                onClick={onComplete} 
                className="w-full py-4 bg-emerald-500 hover:bg-emerald-600"
              >
                Devam Et
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            )}

            {/* Üniteye Dön */}
            {onBackToUnit && (
              <Button 
                onClick={onBackToUnit} 
                variant="outline" 
                className="w-full py-3 border-blue-300 text-blue-600 hover:bg-blue-50"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Üniteye Dön
              </Button>
            )}

            {/* Tekrar Çalış */}
            {reviewCards.length > 0 && (
              <Button onClick={handleReset} variant="outline" className="w-full py-3">
                <RotateCcw className="w-5 h-5 mr-2" />
                Tekrar Çalış
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-lg mx-auto">
      {/* Title & Progress */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-gray-700">{title || 'Flash Kartlar'}</h3>
          <span className="text-sm text-gray-500">{currentIndex + 1} / {cards.length}</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-blue-400 to-blue-600 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Card */}
      <div 
        className="relative h-[300px] cursor-pointer perspective-1000"
        onClick={handleFlip}
      >
        <div className={cn(
          'absolute inset-0 transition-transform duration-500 transform-style-preserve-3d',
          isFlipped && 'rotate-y-180'
        )}>
          {/* Front */}
          <div className={cn(
            'absolute inset-0 bg-white rounded-3xl shadow-xl border border-gray-100 p-8 flex flex-col items-center justify-center backface-hidden',
            isFlipped && 'invisible'
          )}>
            <div className="absolute top-4 right-4">
              <button 
                onClick={(e) => { e.stopPropagation(); speakText(currentCard.front); }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <Volume2 className="w-6 h-6 text-blue-500" />
              </button>
            </div>
            <p className="text-3xl font-bold text-gray-800 text-center">{currentCard.front}</p>
            {currentCard.phonetic && (
              <p className="text-gray-400 mt-2">{currentCard.phonetic}</p>
            )}
            <p className="text-sm text-gray-400 mt-6">Çevirmek için tıklayın</p>
          </div>

          {/* Back */}
          <div className={cn(
            'absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-600 rounded-3xl shadow-xl p-8 flex flex-col items-center justify-center rotate-y-180 backface-hidden',
            !isFlipped && 'invisible'
          )}>
            <p className="text-3xl font-bold text-white text-center mb-4">{currentCard.back}</p>
            {currentCard.example && (
              <div className="bg-white/20 rounded-xl p-3 mt-2">
                <p className="text-white/90 text-sm text-center">
                  <Lightbulb className="w-4 h-4 inline mr-1" />
                  {currentCard.example}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons - Only show when flipped */}
      {isFlipped && (
        <div className="flex gap-3 mt-6">
          <Button
            onClick={handleReview}
            variant="outline"
            className="flex-1 py-6 rounded-xl border-amber-300 text-amber-600 hover:bg-amber-50"
          >
            <RotateCcw className="w-5 h-5 mr-2" />
            Tekrar Et
          </Button>
          <Button
            onClick={handleKnown}
            className="flex-1 py-6 rounded-xl bg-green-500 hover:bg-green-600"
          >
            <CheckCircle2 className="w-5 h-5 mr-2" />
            Biliyorum
          </Button>
        </div>
      )}

      {/* Navigation - Only when not flipped */}
      {!isFlipped && (
        <div className="flex justify-center gap-4 mt-6">
          <Button
            onClick={handlePrevious}
            variant="outline"
            disabled={currentIndex === 0}
            className="rounded-xl"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <Button
            onClick={handleFlip}
            className="px-8 rounded-xl bg-blue-500 hover:bg-blue-600"
          >
            Kartı Çevir
          </Button>
        </div>
      )}

      {/* Stats */}
      <div className="flex justify-center gap-6 mt-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-green-500" />
          <span className="text-gray-600">Biliyorum: {knownCards.length}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-amber-500" />
          <span className="text-gray-600">Tekrar: {reviewCards.length}</span>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{__html: `
        .perspective-1000 { perspective: 1000px; }
        .transform-style-preserve-3d { transform-style: preserve-3d; }
        .backface-hidden { backface-visibility: hidden; }
        .rotate-y-180 { transform: rotateY(180deg); }
      `}} />
    </div>
  );
}
